import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { dataLibraryApi, fetchAllPages, MAX_GROUPED_ROWS } from '../../../services/api/dataLibrary';
import type { DataLibraryCoachSurveyParams } from '../../../services/api/dataLibrary';
import type {
  DataLibraryCoachSurveyRow,
  DataLibrarySurveyQuestion,
  DataLibraryCoachSurveyFilterOptions,
} from '../../../types';
import type { SortConfig, ColumnConfig } from '../../../hooks/useColumnConfig';
import type { ActiveFilterRow } from '../../common/FilterPanel/FilterPanel';
import type { SortState } from '../../../utils/sortTasks';
import type { GroupState } from '../../common/GroupPanel/GroupPanel';
import { groupDataByLevels, type GroupNode } from '../../../utils/groupDataByField';
import { useDataLibraryColumns } from '../useDataLibraryTableLayout';

interface UseDataLibraryCoachMidVsEndProps {
  selectedCohortIds: string[];
  activeGroup: GroupState | null;
  onGroupChange: (group: GroupState | null) => void;
}

/** MED-668: Static columns for Coach Mid vs End tab (MED-750: email first) */
const STATIC_COLUMNS: ColumnConfig[] = [
  { id: 'email', label: 'Email', visible: true, sortable: true, groupable: false },
  { id: 'full_name', label: 'Coach Name', visible: true, sortable: false },
  { id: 'cohort_name', label: 'Cohort', visible: true, sortable: false },
  { id: 'submitted_at', label: 'Submitted', visible: true, sortable: true },
  { id: 'survey_stage', label: 'Survey Stage', visible: true, sortable: false },
  { id: 'overall_satisfaction', label: 'Overall Satisfaction', visible: true, sortable: false },
];

export function useDataLibraryCoachMidVsEnd({
  selectedCohortIds,
  activeGroup,
}: UseDataLibraryCoachMidVsEndProps) {
  const [rows, setRows] = useState<DataLibraryCoachSurveyRow[]>([]);
  const [questions, setQuestions] = useState<DataLibrarySurveyQuestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [filterOptions, setFilterOptions] = useState<DataLibraryCoachSurveyFilterOptions>({
    cohorts: [],
  });
  const [hasSurveyConfig, setHasSurveyConfig] = useState(true);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  // Search
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchValue, 300);

  // Sort
  const [sortConfig, setSortConfig] = useState<SortConfig | null>({
    key: 'first_name',
    order: 'ASC',
  });

  // Filters
  const [activeFilters, setActiveFilters] = useState<ActiveFilterRow[]>([]);

  // Column config
  // Prefix question columns with Mid:/End: based on comparison_pair or display_order
  const defaultColumns: ColumnConfig[] = useMemo(() => {
    const GROUPABLE_QUESTION_TYPES = new Set(['choices', 'yesNo', 'scale']);
    const questionCols: ColumnConfig[] = questions.map(q => ({
      id: `q_${q.question_id}`,
      label: q.question_text,
      visible: true,
      sortable: false,
      groupable: GROUPABLE_QUESTION_TYPES.has(q.question_type),
    }));

    return [...STATIC_COLUMNS, ...questionCols];
  }, [questions]);

  // MDF-209: saved order and visibility for this tab
  const { columns, setColumns } = useDataLibraryColumns(defaultColumns);

  const visibleColumns = useMemo(() => columns.filter(c => c.visible), [columns]);

  // Build API params — fetch both midpoint and endpoint
  const buildParams = useCallback((): DataLibraryCoachSurveyParams => {
    const params: DataLibraryCoachSurveyParams = {
      cohortIds: selectedCohortIds,
      surveyType: 'midpoint', // fallback, not used when surveyTypes is set
      surveyTypes: ['midpoint', 'endpoint'],
      page,
      limit: pageSize,
      ...(sortConfig && { sortBy: sortConfig.key, sortOrder: sortConfig.order }),
    };

    if (debouncedSearch) params.search = debouncedSearch;

    return params;
  }, [selectedCohortIds, page, pageSize, sortConfig, debouncedSearch]);

  // MDF-39: the filtered total when it is over MAX_GROUPED_ROWS; the table then stays paged
  const [overGroupLimit, setOverGroupLimit] = useState<number | null>(null);
  const isGrouped = !!activeGroup && overGroupLimit === null;
  // MDF-39: only the newest request may write state; a grouped load spans many requests
  const latestRequest = useRef(0);

  // Fetch
  const fetchData = useCallback(async () => {
    const requestId = ++latestRequest.current;
    if (selectedCohortIds.length === 0) {
      setRows([]);
      setQuestions([]);
      setTotalCount(0);
      setTotalPages(0);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const params = buildParams();
      // MDF-39: a grouped table needs every row, so it fetches all pages
      const result = isGrouped
        ? await fetchAllPages((page, limit) => dataLibraryApi.getCoachSurveyResponses({ ...params, page, limit }), () => requestId !== latestRequest.current)
        : await dataLibraryApi.getCoachSurveyResponses(params);
      if (requestId !== latestRequest.current) return;
      const total = result.meta.pagination.total;
      setOverGroupLimit(total > MAX_GROUPED_ROWS ? total : null);
      if (isGrouped && total > MAX_GROUPED_ROWS) return;

      setRows(result.data || []);
      setQuestions(result.meta.questions || []);
      setTotalCount(result.meta.pagination.total);
      setTotalPages(result.meta.pagination.totalPages);
      setFilterOptions(result.meta.filterOptions);
      setHasSurveyConfig(result.meta.hasSurveyConfig);
    } catch {
      if (requestId !== latestRequest.current) return;
      notifications.show({
        title: 'Error',
        message: 'Failed to load coach mid vs end responses',
        color: 'red',
      });
    } finally {
      if (requestId === latestRequest.current) setLoading(false);
    }
  }, [selectedCohortIds, buildParams, isGrouped]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, activeFilters, selectedCohortIds]);

  // Sort handlers
  const handleSort = useCallback((key: string) => {
    setSortConfig(prev => ({
      key,
      order: prev?.key === key && prev.order === 'ASC' ? 'DESC' : 'ASC',
    }));
  }, []);

  const handleSortPanelChange = useCallback((sort: SortState | null) => {
    if (sort) {
      setSortConfig({ key: sort.field, order: sort.direction === 'asc' ? 'ASC' : 'DESC' });
    } else {
      setSortConfig(null);
    }
  }, []);

  // Filter handlers
  const handleFilterAdd = useCallback((key: string) => {
    setActiveFilters(prev => {
      if (prev.some(f => f.key === key)) return prev;
      return [...prev, { key, values: [] }];
    });
  }, []);

  const handleFilterChange = useCallback((key: string, values: string[]) => {
    setActiveFilters(prev => prev.map(f => (f.key === key ? { ...f, values } : f)));
  }, []);

  const handleFilterRemove = useCallback((key: string) => {
    setActiveFilters(prev => prev.filter(f => f.key !== key));
  }, []);

  const clearAllFilters = useCallback(() => {
    setActiveFilters([]);
    setSearchValue('');
  }, []);

  const hasActiveFilters =
    activeFilters.some(f => f.values.length > 0) ||
    debouncedSearch.length > 0;

  // Client-side filters
  const filteredRows = useMemo(() => {
    let result = rows;

    for (const filter of activeFilters) {
      if (filter.values.length === 0) continue;
      if (filter.key === 'overall_satisfaction') {
        result = result.filter(r => r.overall_satisfaction && filter.values.includes(r.overall_satisfaction));
      }
      if (filter.key === 'cohort') {
        result = result.filter(r => filter.values.includes(r.cohort_id));
      }
      if (filter.key === 'survey_stage') {
        result = result.filter(r => filter.values.includes(r.survey_stage));
      }
    }

    return result;
  }, [rows, activeFilters]);

  // Client-side grouping — default by coach email (mid + endpoint together)
  const groupedRows = useMemo(() => {
    // Within each group, sort: Midpoint first, then Endpoint. The sort is stable,
    // so sorting before grouping keeps the server order inside each stage.
    const stageOrder: Record<string, number> = { Midpoint: 0, Endpoint: 1 };
    const byStage = [...filteredRows].sort(
      (a, b) => (stageOrder[a.survey_stage] ?? 2) - (stageOrder[b.survey_stage] ?? 2),
    );
    return groupDataByLevels(
      byStage as unknown as Record<string, unknown>[],
      isGrouped ? activeGroup : null,
      columns,
    ) as GroupNode<DataLibraryCoachSurveyRow>[] | null;
  }, [filteredRows, activeGroup, isGrouped, columns]);

  return {
    groupLimitTotal: activeGroup ? overGroupLimit : null,
    rows: filteredRows,
    groupedRows,
    hasSurveyConfig,
    questions,
    loading,
    totalCount,
    totalPages,
    filterOptions,
    page,
    setPage,
    pageSize,
    setPageSize: (size: number) => {
      setPageSize(size);
      setPage(1);
    },
    searchValue,
    setSearchValue,
    sortConfig,
    handleSort,
    handleSortPanelChange,
    activeFilters,
    handleFilterAdd,
    handleFilterChange,
    handleFilterRemove,
    clearAllFilters,
    hasActiveFilters,
    columns,
    setColumns,
    visibleColumns,
  };
}
